/**
 * BulkCampaignActionsPage (FS-814) — multi-select campaign picker + action
 * dropdown + Apply button -> confirmation dialog -> results summary.
 *
 * Visual language: emerald -> sky gradient headline, light theme, zinc-200
 * borders, 6-8px rounded, plain React + Tailwind only. No emojis, no other
 * colours outside the canonical palette.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	BULK_ACTIONS,
	BULK_ACTION_LABELS,
	BULK_ACTION_VERBS,
	DESTRUCTIVE_ACTIONS,
	MAX_CAMPAIGN_IDS,
	isValidBulkAction,
	statusBadgeClass,
	summarizeResponse,
	validateRunBody,
	type BulkAction,
	type CampaignOption,
	type RunBulkActionResponse,
} from "./api";
import {
	useBulkJobsList,
	useCampaignsList,
	useRunBulkAction,
} from "./hooks";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20";
const BTN_PRIMARY =
	"rounded-[6px] border border-sky-600 bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"rounded-[6px] border border-red-600 bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60";

export function BulkCampaignActionsPage() {
	const campaignsQ = useCampaignsList();
	const jobsQ = useBulkJobsList();
	const runMut = useRunBulkAction();

	const [selected, setSelected] = useState<Set<string>>(new Set());
	const [action, setAction] = useState<BulkAction | "">("");
	const [filter, setFilter] = useState("");
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [lastResult, setLastResult] = useState<RunBulkActionResponse | null>(
		null,
	);
	const [validation, setValidation] = useState<{
		action?: string;
		campaignIds?: string;
	}>({});

	const campaigns: CampaignOption[] = useMemo(() => {
		return campaignsQ.data?.campaigns ?? [];
	}, [campaignsQ.data]);

	const filtered = useMemo(() => {
		const needle = filter.trim().toLowerCase();
		if (!needle) return campaigns;
		return campaigns.filter((c) => {
			return (
				c.id.toLowerCase().includes(needle) ||
				c.name.toLowerCase().includes(needle)
			);
		});
	}, [campaigns, filter]);

	const allSelected =
		filtered.length > 0 && filtered.every((c) => selected.has(c.id));

	const toggle = (id: string) => {
		setSelected((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	const toggleAll = () => {
		setSelected((prev) => {
			const next = new Set(prev);
			if (allSelected) {
				for (const c of filtered) next.delete(c.id);
			} else {
				for (const c of filtered) next.add(c.id);
			}
			return next;
		});
	};

	const clearSelection = () => setSelected(new Set());

	const handleApply = (e: FormEvent) => {
		e.preventDefault();
		setLastResult(null);
		const result = validateRunBody({
			campaignIds: Array.from(selected),
			action: action === "" ? null : action,
		});
		if (!result.ok) {
			setValidation(result.errors);
			return;
		}
		setValidation({});
		setConfirmOpen(true);
	};

	const handleConfirm = () => {
		if (!isValidBulkAction(action)) return;
		const body = {
			campaignIds: Array.from(selected),
			action: action as BulkAction,
		};
		runMut.mutate(body, {
			onSuccess: (res) => {
				setLastResult(res);
				setConfirmOpen(false);
				setSelected(new Set());
				setAction("");
			},
		});
	};

	const handleCancel = () => {
		setConfirmOpen(false);
	};

	const selectedCount = selected.size;
	const actionValid = isValidBulkAction(action);
	const destructive = actionValid && DESTRUCTIVE_ACTIONS.has(action);
	const jobs = jobsQ.data?.jobs ?? [];

	return (
		<main
			data-testid="bulk-campaign-actions-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold text-transparent">
					Bulk campaign actions
				</h1>
				<p className="text-sm text-zinc-600">
					Select up to {MAX_CAMPAIGN_IDS} campaigns, pick an action,
					and apply in one call.
				</p>
			</header>

			<form
				onSubmit={handleApply}
				className={`${CARD} flex flex-col gap-5`}
				data-testid="bulk-campaign-actions-form"
				aria-label="bulk-campaign-actions form"
			>
				<div className="flex flex-col gap-1.5">
					<label
						htmlFor="bulk-campaign-actions-filter"
						className={LABEL}
					>
						Filter campaigns
					</label>
					<input
						id="bulk-campaign-actions-filter"
						type="text"
						className={INPUT}
						value={filter}
						placeholder="name or id"
						onChange={(e) => setFilter(e.currentTarget.value)}
						data-testid="bulk-campaign-actions-filter-input"
					/>
				</div>

				<div className="flex flex-col gap-2">
					<div className="flex items-center justify-between">
						<span className={LABEL}>
							Campaigns ({selectedCount} selected)
						</span>
						<div className="flex items-center gap-2">
							<button
								type="button"
								className={BTN_GHOST}
								onClick={toggleAll}
								disabled={filtered.length === 0}
								data-testid="bulk-campaign-actions-toggle-all"
							>
								{allSelected ? "Unselect all" : "Select all"}
							</button>
							<button
								type="button"
								className={BTN_GHOST}
								onClick={clearSelection}
								disabled={selectedCount === 0}
								data-testid="bulk-campaign-actions-clear"
							>
								Clear
							</button>
						</div>
					</div>

					<div
						className="max-h-64 overflow-y-auto rounded-[6px] border border-zinc-200"
						data-testid="bulk-campaign-actions-list"
					>
						{campaignsQ.isLoading ? (
							<p
								className="px-3 py-4 text-sm text-zinc-500"
								data-testid="bulk-campaign-actions-loading"
							>
								Loading campaigns.
							</p>
						) : filtered.length === 0 ? (
							<p
								className="px-3 py-4 text-sm text-zinc-500"
								data-testid="bulk-campaign-actions-empty"
							>
								No campaigns match.
							</p>
						) : (
							<ul className="divide-y divide-zinc-100">
								{filtered.map((c) => {
									const checked = selected.has(c.id);
									return (
										<li
											key={c.id}
											className="flex items-center gap-3 px-3 py-2 hover:bg-zinc-50"
										>
											<input
												id={`camp-${c.id}`}
												type="checkbox"
												className="h-4 w-4 rounded border-zinc-300 text-sky-600 focus:ring-sky-500"
												checked={checked}
												onChange={() => toggle(c.id)}
												data-testid={`bulk-campaign-actions-checkbox-${c.id}`}
											/>
											<label
												htmlFor={`camp-${c.id}`}
												className="flex flex-1 flex-col cursor-pointer"
											>
												<span className="text-sm font-medium text-zinc-900">
													{c.name}
												</span>
												<span className="text-xs text-zinc-500">
													{c.id}
												</span>
											</label>
										</li>
									);
								})}
							</ul>
						)}
					</div>

					{validation.campaignIds ? (
						<p
							className="text-sm text-red-600"
							role="alert"
							data-testid="bulk-campaign-actions-campaigns-error"
						>
							{validation.campaignIds}
						</p>
					) : null}
				</div>

				<div className="flex flex-col gap-1.5">
					<label
						htmlFor="bulk-campaign-actions-action"
						className={LABEL}
					>
						Action
					</label>
					<select
						id="bulk-campaign-actions-action"
						className={INPUT}
						value={action}
						onChange={(e) =>
							setAction(e.currentTarget.value as BulkAction | "")
						}
						data-testid="bulk-campaign-actions-action-select"
					>
						<option value="">Choose an action</option>
						{BULK_ACTIONS.map((a) => (
							<option key={a} value={a}>
								{BULK_ACTION_LABELS[a]}
							</option>
						))}
					</select>
					{validation.action ? (
						<p
							className="text-sm text-red-600"
							role="alert"
							data-testid="bulk-campaign-actions-action-error"
						>
							{validation.action}
						</p>
					) : null}
				</div>

				<div className="flex items-center gap-3">
					<button
						type="submit"
						className={BTN_PRIMARY}
						disabled={
							runMut.isPending ||
							selectedCount === 0 ||
							!actionValid
						}
						data-testid="bulk-campaign-actions-apply"
					>
						Apply
					</button>
					{runMut.error ? (
						<p
							className="text-sm text-red-600"
							role="alert"
							data-testid="bulk-campaign-actions-run-error"
						>
							{runMut.error.message}
						</p>
					) : null}
				</div>
			</form>

			{lastResult ? (
				<section
					className={CARD}
					data-testid="bulk-campaign-actions-summary"
					aria-live="polite"
				>
					<h2 className="mb-3 text-lg font-semibold text-zinc-900">
						Last run
					</h2>
					<p className="text-sm text-zinc-700">
						{summarizeResponse(lastResult)}
					</p>
					<p className="mt-1 text-xs text-zinc-500">
						Job id: {lastResult.jobId}
					</p>
					{lastResult.rejected.length > 0 ? (
						<div className="mt-3">
							<p className="text-sm font-medium text-zinc-800">
								Rejected
							</p>
							<ul
								className="mt-1 text-sm text-red-700"
								data-testid="bulk-campaign-actions-rejected-list"
							>
								{lastResult.rejected.map((r) => (
									<li key={r.campaign_id}>
										{r.campaign_id}: {r.error}
									</li>
								))}
							</ul>
						</div>
					) : null}
				</section>
			) : null}

			<section
				className={CARD}
				data-testid="bulk-campaign-actions-jobs"
				aria-label="recent bulk jobs"
			>
				<h2 className="mb-3 text-lg font-semibold text-zinc-900">
					Recent jobs
				</h2>
				{jobsQ.isLoading ? (
					<p className="text-sm text-zinc-500">Loading jobs.</p>
				) : jobs.length === 0 ? (
					<p
						className="text-sm text-zinc-500"
						data-testid="bulk-campaign-actions-jobs-empty"
					>
						No jobs yet.
					</p>
				) : (
					<ul className="divide-y divide-zinc-100">
						{jobs.map((j) => (
							<li
								key={j.id}
								className="flex items-center justify-between gap-3 py-2"
							>
								<div className="flex flex-col">
									<span className="text-sm font-medium text-zinc-900">
										{BULK_ACTION_LABELS[j.action]}
										{" - "}
										{j.campaign_ids.length}{" "}
										{j.campaign_ids.length === 1
											? "campaign"
											: "campaigns"}
									</span>
									<span className="text-xs text-zinc-500">
										{j.accepted} accepted,{" "}
										{j.rejected} rejected
									</span>
								</div>
								<span
									className={`rounded-[6px] border px-2 py-1 text-xs font-medium ${statusBadgeClass(j.status)}`}
								>
									{j.status}
								</span>
							</li>
						))}
					</ul>
				)}
			</section>

			{confirmOpen ? (
				<div
					className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 p-4"
					role="dialog"
					aria-modal="true"
					aria-labelledby="bulk-confirm-title"
					data-testid="bulk-campaign-actions-confirm"
				>
					<div className="w-full max-w-md rounded-[8px] border border-zinc-200 bg-white p-6 shadow-lg">
						<h2
							id="bulk-confirm-title"
							className="text-lg font-semibold text-zinc-900"
						>
							Confirm bulk action
						</h2>
						<p className="mt-2 text-sm text-zinc-700">
							{selectedCount}{" "}
							{selectedCount === 1 ? "campaign" : "campaigns"}{" "}
							will be{" "}
							{actionValid
								? BULK_ACTION_VERBS[action as BulkAction]
								: "updated"}
							.
							{destructive
								? " This action is not reversible."
								: ""}
						</p>
						<div className="mt-5 flex items-center justify-end gap-2">
							<button
								type="button"
								className={BTN_GHOST}
								onClick={handleCancel}
								disabled={runMut.isPending}
								data-testid="bulk-campaign-actions-confirm-cancel"
							>
								Cancel
							</button>
							<button
								type="button"
								className={destructive ? BTN_DANGER : BTN_PRIMARY}
								onClick={handleConfirm}
								disabled={runMut.isPending}
								data-testid="bulk-campaign-actions-confirm-ok"
							>
								{runMut.isPending ? "Applying." : "Confirm"}
							</button>
						</div>
					</div>
				</div>
			) : null}
		</main>
	);
}

export default BulkCampaignActionsPage;
